import { X } from "lucide-react";
import { useCallback, useEffect } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface ChangelogEntry {
  slug: string;
  title: string;
  content: string;
  publishedAt: string;
}

interface ChangelogModalProps {
  entries: ChangelogEntry[];
  onClose: () => void;
  title?: string;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function ChangelogModal({ entries, onClose, title = "What's New" }: ChangelogModalProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="mx-4 flex max-h-[80vh] w-full max-w-2xl flex-col rounded-lg bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold text-text-primary">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-text-muted transition-colors hover:bg-card-hover hover:text-text-secondary"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-4">
          {entries.length === 0 ? (
            <p className="py-8 text-center text-sm text-text-muted">No changelog entries yet.</p>
          ) : (
            entries.map((entry) => (
              <article key={entry.slug} className="border-b border-border pb-6 last:border-b-0">
                <div className="mb-2 flex items-baseline justify-between gap-4">
                  <h3 className="text-base font-semibold text-text-primary">{entry.title}</h3>
                  <time className="shrink-0 text-xs text-text-faint">
                    {formatDate(entry.publishedAt)}
                  </time>
                </div>
                <div className="prose prose-sm prose-neutral dark:prose-invert max-w-none">
                  <Markdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      a: ({ href, children }) => (
                        <a href={href} target="_blank" rel="noopener noreferrer">
                          {children}
                        </a>
                      ),
                    }}
                  >
                    {entry.content}
                  </Markdown>
                </div>
              </article>
            ))
          )}
        </div>

        <div className="border-t border-border px-6 py-3">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
